import { axiosInstance } from "@/lib/axiosInstance";
import axios from "axios";

export type LogoutHandler = () => void;

export type AuthUserType = {
  id: string;
  recruiter_id: string;
  name: string;
  org_email: string;
  personal_email: string;
  designation: string;
  designation_id: string;
  designation_name: string;
  designation_rank: number;
  permissions: string[];
  role_ids: string[];
};

export type AuthResponse = {
  success: boolean;
  message: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  data: AuthUserType;
  stream_id?: string;
  token?: string; // keeping for backward compatibility with older Google Auth data if needed
  user?: AuthUserType;
};


/**
 * POST /v1/register response. Registration is auto-approved since backend
 * c819480: `status` comes back "provisioned", not "pending", and the admin
 * can log in straight away. `id` is kept for backward compatibility.
 */
export type RegisterResponse = {
  id: string;
  registration_id: string;
  recruiter_id: string;
  staff_id: string;
  company_name: string;
  company_email: string;
  status: string;
  message?: string;
};

class AuthService {
  private accessKey = "authToken";
  private refreshKey = "refreshToken";
  private userKey = "authUser";
  private logoutHandlers: LogoutHandler[] = [];

  getAccessToken(): string | null {
    return sessionStorage.getItem(this.accessKey); // need to change to session storage
  }
  setAccessToken(token: string) {
    sessionStorage.setItem(this.accessKey, token); // need to change to session storage
  }
  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshKey);
  }
  setRefreshToken(token: string) {
    localStorage.setItem(this.refreshKey, token);
  }
  getUser(): AuthUserType | null {
    const user = sessionStorage.getItem(this.userKey);
    return user ? JSON.parse(user) : null;
  }
  setUser(user: AuthUserType) {
    sessionStorage.setItem(this.userKey, JSON.stringify(user));
  }
  clear() {
    sessionStorage.removeItem(this.accessKey); // need to change to session storage
    sessionStorage.removeItem(this.userKey);
    localStorage.removeItem(this.refreshKey);
  }
  onLogout(handler: LogoutHandler) {
    this.logoutHandlers.push(handler);
    return () => {
      this.logoutHandlers = this.logoutHandlers.filter((h) => h !== handler);
    };
  }
  async triggerLogout() {
    try {
      axiosInstance.post("/auth/logout").catch((error) => {
        console.error("Error during logout request:", error);
      });
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      this.clear();
      this.logoutHandlers.forEach((h) => h());
    }
  }

  async sendOtp(type: "email" | "phone", identifier: string) {
    const { data } = await axiosInstance.post("/v1/auth/send-otp", {
      type,
      identifier,
    });
    return data;
  }

  async verifyOtp(type: "email" | "phone", identifier: string, otp: string) {
    const { data } = await axiosInstance.post("/v1/auth/verify-otp", {
      type,
      identifier,
      otp,
    });
    if (data.access_token) {
      this.setAccessToken(data.access_token);
      if (data.refresh_token) {
        this.setRefreshToken(data.refresh_token);
      }
    }
    return data as AuthResponse;
  }

  async registerCollege(payload: {
    company_name: string;
    company_email: string;
    company_phone: string;
    company_address: string;
    company_website?: string;
    admin_name: string;
    admin_org_email: string;
    admin_personal_email?: string;
    admin_phone: string;
  }) {
    const { data } = await axiosInstance.post<RegisterResponse>(
      "/v1/register",
      payload
    );
    return data;
  }

  // login with google
  async loginWithGoogle(googleAccessToken: string) {
    try {
      const { data } = await axiosInstance.post("/v1/auth/google-login", {
        google_access_token: googleAccessToken,
      });

      // // Check if user is admin
      // if (data?.user?.role !== "admin" || !data?.token) {
      //   this.clear();
      //   throw new Error(
      //     "Access denied. Admin role required or access token is not present in the response"
      //   );
      // }

      // // Save token
      // if (data.token) {
      //   this.setAccessToken(data.token);
      //   // Optional: if backend returns refresh token
      //   // this.setRefreshToken(data.refreshToken);
      // }

      return data as AuthResponse;
    } catch (error: unknown) {
      this.clear();
      throw error;
    }
  }

  // Basic refresh implementation — uses native fetch to avoid circular axios import
  async refreshToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) throw new Error("No refresh token");

    const baseUrl = import.meta.env.VITE_API_URL;

    if (!baseUrl) {
      throw new Error("base url is missing");
    }

    const { data } = await axios.post(
      `${import.meta.env.VITE_API_URL}/auth/refresh`, // need to change depending upon the backend route
      { token: refreshToken }
    );

    if (!data?.accessToken) {
      this.triggerLogout();
      throw new Error("Invalid refresh response");
    }
    this.setAccessToken(data.accessToken);
    return data.accessToken;
  }
}

export const authService = new AuthService();
